/**
 * Track A — Lab sonification params (browser-only).
 * SoT for AudioParams; not nested under RenderParams.
 */

import { keysToCamel } from "../core/snakeCamel";

/** Track A — Lab sonification (browser-only). */
export type AudioParams = {
  /** Master enable. Off by default. Does not imply AudioContext exists. */
  enabled: boolean;
  /** Linear master gain 0…1 after voice mix. Default 0.32 (museum-safe). */
  masterGain: number;
  /**
   * How far features may travel along the scale (melody range).
   * 0 ≈ home register; 1 = full mapped walk. Default 0.3.
   */
  pitchAmount: number;
  /**
   * Particle subsample step for extractors.
   * 0 = auto via strideForN(n); positive k = sample every k-th particle.
   */
  particleStride: number;
  kinematicsVoice: boolean;
  gravityVoice: boolean;
  topologyVoice: boolean;
  /** v1.5 forward-compat; default false; no v1 UI. */
  socialVoice: boolean;
  /**
   * Reserved soft ceiling for future evented voices.
   * Continuous pads ignore this; kept for URL/schema stability.
   */
  maxEventsPerTick: number;
};

export const defaultAudioParams = (): AudioParams => ({
  enabled: false,
  // Soft default — multi-osc pads sum; leave headroom so blend stays pleasant
  masterGain: 0.32,
  // Melody range width (continuous scale travel); beds stay amplitude-smooth
  pitchAmount: 0.3,
  particleStride: 0, // auto
  kinematicsVoice: true,
  gravityVoice: false,
  topologyVoice: true,
  socialVoice: false,
  maxEventsPerTick: 24,
});

export function validateAudioParams(a: AudioParams): AudioParams {
  const clamp01 = (x: number) =>
    Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : 0;
  let stride = Number(a.particleStride);
  if (!Number.isFinite(stride) || stride < 0) stride = 0;
  stride = Math.floor(stride);
  let maxEv = Number(a.maxEventsPerTick);
  if (!Number.isFinite(maxEv)) maxEv = 24;
  maxEv = Math.max(1, Math.min(128, Math.floor(maxEv)));
  return {
    enabled: Boolean(a.enabled),
    masterGain: clamp01(Number(a.masterGain)),
    pitchAmount: clamp01(Number(a.pitchAmount)),
    particleStride: stride,
    kinematicsVoice: Boolean(a.kinematicsVoice),
    gravityVoice: Boolean(a.gravityVoice),
    topologyVoice: Boolean(a.topologyVoice),
    socialVoice: Boolean(a.socialVoice),
    maxEventsPerTick: maxEv,
  };
}

export function cloneAudioParams(a: AudioParams): AudioParams {
  return { ...a };
}

/** Merge snake_case partial (catalog / URL) onto defaults. */
export function audioParamsFromSnake(
  partial: Record<string, unknown> | undefined | null,
): AudioParams {
  const camel = keysToCamel(partial ?? {}) as Partial<AudioParams>;
  return validateAudioParams({ ...defaultAudioParams(), ...camel });
}
